import { BrowserDownloader, type DownloadProgressCallback } from '@/services/BrowserDownloader';
import { getFileExtension } from '@/utils/urls';
import { DATE_FORMATS } from '@/consts/date.consts';
import { formatInGMT } from '@/utils/date.utils';
import type { DownloadableAsset, FolderEntry } from '@/types/downloadable';
import type { VideoExportFormat } from '../Views/ExportView';

const DEFAULT_WORKFLOW_NAME = 'workflow';
export const downloader = new BrowserDownloader();

export const VIDEO_FORMAT_MIME_TYPES: Record<VideoExportFormat, string> = {
  mp4: 'video/mp4',
  'mov-prores': 'video/quicktime',
  'mov-h264': 'video/quicktime',
  webm: 'video/webm',
};

/** Strips the codec suffix from `mov-*` so the file is saved with the bare `.mov` extension. */
export function videoFormatToExtension(format: VideoExportFormat): 'mp4' | 'mov' | 'webm' {
  return format === 'mov-prores' || format === 'mov-h264' ? 'mov' : format;
}

/**
 * Builds the timestamped, prefixed filename used for node exports
 * (`{prefix}_{nodeName}_{YYYY-MM-DDTHH-MM-SS}{.ext}`).
 */
export function buildExportFileName(opts: { prefix?: string; nodeName: string; extension?: string }): string {
  const { prefix = DEFAULT_WORKFLOW_NAME, nodeName, extension } = opts;
  const timestamp = formatInGMT(new Date(), DATE_FORMATS.FILE_TIMESTAMP);
  return `${prefix}_${nodeName}_${timestamp}${extension ? `.${extension}` : ''}`;
}

interface DownloadAssetOptions {
  url?: string;
  blob?: Blob;
  fileType: string;
  nodeName: string;
  prefix?: string;
  signal?: AbortSignal;
  /** Explicit extension (no leading dot). When set, overrides URL/fileType-derived guesses.
   *  Use when the source has an authoritative answer (e.g. server's Content-Disposition). */
  extension?: string;
}

/**
 * Downloads a single asset with timestamped filename.
 * Handles both URLs and Blobs. For Blobs, creates a temporary blob URL
 * that is automatically cleaned up after download.
 */
export async function downloadNodeAsset(options: DownloadAssetOptions): Promise<void> {
  const { url, blob, fileType, nodeName, prefix, signal, extension: explicitExtension } = options;
  const extension = explicitExtension || getFileExtension(url || '', fileType);
  const fileName = buildExportFileName({ prefix, nodeName, extension });

  // If we have a blob, create URL only for the download operation
  if (blob) {
    const blobUrl = URL.createObjectURL(blob);
    try {
      await downloader.downloadFile(blobUrl, fileName, fileType, signal);
    } finally {
      // Cleanup happens immediately after download - no memory leak possible
      URL.revokeObjectURL(blobUrl);
    }
  } else if (url) {
    await downloader.downloadFile(url, fileName, fileType, signal);
  }
}

/**
 * Downloads multiple assets as a ZIP file.
 * Handles both URLs and Blobs. For Blobs, creates temporary blob URLs
 * that are automatically cleaned up after download.
 */
export async function downloadMultipleAssetsFromNode(
  assets: DownloadableAsset[],
  nodeName: string,
  prefix: string = DEFAULT_WORKFLOW_NAME,
  batchNumber?: number,
  onProgress?: DownloadProgressCallback,
  signal?: AbortSignal,
): Promise<void> {
  const blobUrlsToCleanup: string[] = [];

  try {
    const timestamp = formatInGMT(new Date(), DATE_FORMATS.FILE_TIMESTAMP);
    const collectedFiles = assets.map((asset, index) => {
      const extension = asset.extension || getFileExtension(asset.url || '', asset.type);
      const fileIdentifier = String(index + 1).padStart(4, '0');
      const fileName = `${nodeName}${batchNumber !== undefined ? `_batch_${batchNumber}` : ''}_${fileIdentifier}${extension ? `.${extension}` : ''}`;

      // If asset has a blob, create a temporary URL for it
      let url = asset.url;
      if (asset.blob) {
        url = URL.createObjectURL(asset.blob);
        blobUrlsToCleanup.push(url);
      }

      return {
        type: 'file' as const,
        name: fileName,
        url: url || '',
        sizeBytes: asset.sizeBytes,
      };
    });

    if (collectedFiles.length === 0) {
      return;
    }

    const folderName =
      batchNumber !== undefined
        ? `${prefix}_${nodeName}_batch_${batchNumber}_${timestamp}`
        : `${prefix}_${nodeName}_${timestamp}`;

    const rootFolder: FolderEntry = {
      type: 'folder',
      name: folderName,
      children: collectedFiles,
    };

    await downloader.downloadFolder(rootFolder, onProgress, signal);
  } finally {
    // Clean up all blob URLs created during this operation
    for (const url of blobUrlsToCleanup) {
      URL.revokeObjectURL(url);
    }
  }
}

/**
 * Downloads assets from multiple nodes as a folder structure in a ZIP.
 * Handles both URLs and Blobs. For Blobs, creates temporary blob URLs
 * that are automatically cleaned up after download.
 */
export async function downloadFromMultipleNodes(
  nodesWithAssets: Array<{
    nodeName: string;
    assets: DownloadableAsset[];
  }>,
  prefix: string = DEFAULT_WORKFLOW_NAME,
  onProgress?: DownloadProgressCallback,
  signal?: AbortSignal,
): Promise<void> {
  const timestamp = formatInGMT(new Date(), DATE_FORMATS.FILE_TIMESTAMP);
  const blobUrlsToCleanup: string[] = [];

  try {
    const nodeNameCounts = new Map<string, number>();
    const nodeFolders: FolderEntry[] = nodesWithAssets
      .filter(({ assets }) => assets.length > 0)
      .map(({ nodeName, assets }) => {
        nodeNameCounts.set(nodeName, (nodeNameCounts.get(nodeName) || 0) + 1);
        return {
          type: 'folder' as const,
          name: `${prefix}_${nodeName}_${nodeNameCounts.get(nodeName)!.toString().padStart(2, '0')}`,
          children: assets.map((asset, index) => {
            const extension = asset.extension || getFileExtension(asset.url || '', asset.type);

            // If asset has a blob, create a temporary URL for it
            let url = asset.url;
            if (asset.blob) {
              url = URL.createObjectURL(asset.blob);
              blobUrlsToCleanup.push(url);
            }

            return {
              type: 'file' as const,
              name: `${prefix}_${nodeName}_${String(index + 1).padStart(4, '0')}${extension ? `.${extension}` : ''}`,
              url: url || '',
              sizeBytes: asset.sizeBytes,
            };
          }),
        };
      });

    nodeFolders.sort((a, b) => a.name.localeCompare(b.name));

    if (nodeFolders.length === 0) return;

    const rootFolder: FolderEntry = {
      type: 'folder',
      name: `${prefix}_${timestamp}`,
      children: nodeFolders,
    };

    await downloader.downloadFolder(rootFolder, onProgress, signal);
  } finally {
    // Clean up all blob URLs created during this operation
    for (const url of blobUrlsToCleanup) {
      URL.revokeObjectURL(url);
    }
  }
}
